/**
 * Riyadh-pinned date/time display helpers.
 *
 * Most merchants are in Saudi Arabia and want timestamps shown in
 * Asia/Riyadh (UTC+3, no DST), regardless of the browser timezone.
 *
 * Use these for *display only*. Do not use for wire format or for keys
 * that are submitted back to the backend — the backend stores instants
 * in UTC and we don't want to TZ-shift those.
 */

export const RIYADH_TZ = 'Asia/Riyadh';

export type DateInput = Date | string | number;

function toDate(input: DateInput): Date {
  return input instanceof Date ? input : new Date(input);
}

/**
 * Build a Date whose **local** Y/M/D/H/M/S match the Riyadh wall clock
 * for the given instant. Useful as a bridge for libraries (e.g. date-fns
 * `format`) that don't accept a `timeZone` option — pass the bridge Date
 * to such a library and the local-based formatter will emit Riyadh time.
 *
 * Why: date-fns v4 has no timezone support without `date-fns-tz`. Rather
 * than add a dependency we extract Riyadh parts via Intl and rebuild.
 */
export function toRiyadhWallClockDate(input: DateInput): Date {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return d;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: RIYADH_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  // Intl returns "24" for midnight in some runtimes; normalise to 0.
  const hour = get('hour') === 24 ? 0 : get('hour');
  return new Date(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
}

/** Locale used for the human-readable output. Arabic-first to match the rest of the dashboard. */
const DEFAULT_LOCALE = 'en-US';

const baseOpts = (extra: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions => ({
  timeZone: RIYADH_TZ,
  ...extra,
});

/** "May 7, 2026" */
export function formatRiyadhDate(
  input: DateInput,
  opts: Intl.DateTimeFormatOptions = {},
  locale: string = DEFAULT_LOCALE,
): string {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(
    locale,
    baseOpts({ year: 'numeric', month: 'short', day: 'numeric', ...opts }),
  ).format(d);
}

/** "08:30 PM" */
export function formatRiyadhTime(
  input: DateInput,
  opts: Intl.DateTimeFormatOptions = {},
  locale: string = DEFAULT_LOCALE,
): string {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(
    locale,
    baseOpts({ hour: '2-digit', minute: '2-digit', hour12: true, ...opts }),
  ).format(d);
}

/** "May 7, 2026, 08:30 PM" */
export function formatRiyadhDateTime(
  input: DateInput,
  opts: Intl.DateTimeFormatOptions = {},
  locale: string = DEFAULT_LOCALE,
): string {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(
    locale,
    baseOpts({
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...opts,
    }),
  ).format(d);
}

/**
 * "2 hours ago" — pure ms-diff so the result is timezone-independent
 * (the *quantity* "2 hours" doesn't depend on TZ). "Now" is the wall
 * clock, which is the same instant in any TZ.
 */
export function formatRiyadhRelative(
  input: DateInput,
  locale: string = DEFAULT_LOCALE,
): string {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = d.getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(diffMs);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;
  const pick = (unit: Intl.RelativeTimeFormatUnit, ms: number) =>
    rtf.format(Math.round(diffMs / ms), unit);
  if (abs < minute) return pick('second', 1000);
  if (abs < hour) return pick('minute', minute);
  if (abs < day) return pick('hour', hour);
  if (abs < week) return pick('day', day);
  if (abs < month) return pick('week', week);
  if (abs < year) return pick('month', month);
  return pick('year', year);
}
